import { getStaticCredentialForm } from "@app/components/actions/mcp/create/static_credential_forms";
import { submitConnectMCPServerDialogForm } from "@app/components/actions/mcp/forms/submitConnectMCPServerDialogForm";
import type { MCPServerOAuthFormValues } from "@app/components/actions/mcp/forms/types";
import { mcpServerOAuthFormSchema } from "@app/components/actions/mcp/forms/types";
import { getConnectMCPServerDialogDefaultValues } from "@app/components/actions/mcp/forms/utils";
import type {
  StaticCredentialConfig,
  StaticCredentialFormHandle,
} from "@app/components/actions/mcp/MCPServerAuthConnection";
import { MCPServerAuthConnection } from "@app/components/actions/mcp/MCPServerAuthConnection";
import type {
  CustomResourceIconType,
  InternalAllowedIconType,
} from "@app/components/resources/resources_icons";
import { getAvatarFromIcon } from "@app/components/resources/resources_icons";
import { FormProvider } from "@app/components/sparkle/FormProvider";
import {
  useSendApiErrorNotification,
  useSendNotification,
} from "@app/hooks/useNotification";
import {
  getMcpServerViewDisplayName,
  getServerTypeAndIdFromSId,
  isRemoteMCPServerType,
} from "@app/lib/actions/mcp_helper";
import { DEFAULT_MCP_SERVER_ICON } from "@app/lib/actions/mcp_icons";
import type { AuthorizationInfo } from "@app/lib/actions/mcp_metadata_extraction";
import type { MCPServerViewType } from "@app/lib/api/mcp";
import { useCellContext } from "@app/lib/auth/CellContext";
import {
  useCreateMCPServerConnection,
  useDiscoverOAuthMetadata,
  useUpdateMCPServerView,
} from "@app/lib/swr/mcp_servers";
import datadogLogger from "@app/logger/datadogLogger";
import type { MCPOAuthUseCase } from "@app/types/oauth/lib";
import {
  OAUTH_PROVIDER_NAMES,
  providerUsesWellKnownOAuthDiscovery,
  validateOAuthCredentials,
} from "@app/types/oauth/lib";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { WorkspaceType } from "@app/types/user";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

interface ConnectMCPServerDialogProps {
  owner: WorkspaceType;
  mcpServerView: MCPServerViewType;
  setIsLoading: (isCreating: boolean) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Prefills and preserves the credential mode when refreshing an existing connection. */
  initialUseCase?: MCPOAuthUseCase | null;
  /** When true (Refresh), hide the use-case picker and keep the existing mode. */
  lockUseCase?: boolean;
}

export function ConnectMCPServerDialog({
  owner,
  mcpServerView,
  setIsLoading: setExternalIsLoading,
  isOpen = false,
  setIsOpen,
  initialUseCase = null,
  lockUseCase = false,
}: ConnectMCPServerDialogProps) {
  const { t } = useLingui();
  const sendNotification = useSendNotification();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const cellContext = useCellContext();

  const defaultValues = useMemo(
    () => getConnectMCPServerDialogDefaultValues(initialUseCase),
    [initialUseCase]
  );
  const form = useForm<MCPServerOAuthFormValues>({
    resolver: zodResolver(mcpServerOAuthFormSchema),
    defaultValues,
    mode: "onChange",
    shouldUnregister: false,
  });

  const useCase = useWatch({
    control: form.control,
    name: "useCase",
  });

  const authCredentials = useWatch({
    control: form.control,
    name: "authCredentials",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [
    remoteMCPServerOAuthDiscoveryDone,
    setRemoteMCPServerOAuthDiscoveryDone,
  ] = useState(false);
  const [authorization, setAuthorization] = useState<AuthorizationInfo | null>(
    null
  );

  const { createMCPServerConnection } = useCreateMCPServerConnection({
    owner,
    connectionType: "workspace",
  });
  const { discoverOAuthMetadataForServer } = useDiscoverOAuthMetadata(owner);
  const { updateServerView } = useUpdateMCPServerView(owner, mcpServerView);

  const serverType = useMemo(
    () => getServerTypeAndIdFromSId(mcpServerView.server.sId).serverType,
    [mcpServerView]
  );

  const toolName = useMemo(() => {
    if (mcpServerView.server) {
      return getMcpServerViewDisplayName(mcpServerView);
    }
    return t`MCP server`;
  }, [mcpServerView, t]);

  const toolIcon: InternalAllowedIconType | CustomResourceIconType =
    useMemo(() => {
      if (mcpServerView.server) {
        return mcpServerView.server.icon;
      }
      return DEFAULT_MCP_SERVER_ICON;
    }, [mcpServerView]);

  // Discover OAuth metadata for remote MCP servers.
  useEffect(() => {
    const discoverOAuth = async () => {
      if (isOpen && mcpServerView) {
        if (serverType === "internal") {
          setAuthorization(mcpServerView.server.authorization);
        } else if (
          isRemoteMCPServerType(mcpServerView.server) &&
          mcpServerView.server.url &&
          !remoteMCPServerOAuthDiscoveryDone
        ) {
          // Skip discovery for providers whose endpoints are not discovered via
          // `.well-known` — their credentials are admin-provided and their
          // endpoints are known or derived.
          const provider = mcpServerView.server.authorization?.provider;
          if (provider && !providerUsesWellKnownOAuthDiscovery(provider)) {
            setAuthorization(mcpServerView.server.authorization);
            setRemoteMCPServerOAuthDiscoveryDone(true);
            setIsLoading(false);
            return;
          }
          setIsLoading(true);
          const discoverOAuthMetadataRes = await discoverOAuthMetadataForServer(
            mcpServerView.server.sId
          );

          if (
            discoverOAuthMetadataRes.isOk() &&
            discoverOAuthMetadataRes.value.oauthRequired
          ) {
            setAuthorization({
              provider: "mcp",
              supported_use_cases: ["platform_actions", "personal_actions"],
            });
            form.setValue("authCredentials", {
              ...discoverOAuthMetadataRes.value.connectionMetadata,
            });
            setRemoteMCPServerOAuthDiscoveryDone(true);
          } else if (discoverOAuthMetadataRes.isErr()) {
            // Refresh must still work when the server is unreachable (e.g. expired
            // admin token): fall back to the authorization already stored on the
            // server so the admin can re-run the auth flow with the same settings.
            if (mcpServerView.server.authorization) {
              setAuthorization(mcpServerView.server.authorization);
              setRemoteMCPServerOAuthDiscoveryDone(true);
            } else {
              sendApiErrorNotification({
                title: t`Failed to discover OAuth metadata for MCP server`,
                error: discoverOAuthMetadataRes.error,
              });
            }
          }
        }
        setIsLoading(false);
      } else {
        setAuthorization(null);
      }
    };

    void discoverOAuth();
  }, [
    mcpServerView,
    isOpen,
    serverType,
    remoteMCPServerOAuthDiscoveryDone,
    discoverOAuthMetadataForServer,
    form,
    sendApiErrorNotification,
    t,
  ]);

  const resetState = () => {
    setExternalIsLoading(false);
    form.reset(getConnectMCPServerDialogDefaultValues(initialUseCase));
    setIsLoading(false);
    setIsStaticFormValid(false);
    setRemoteMCPServerOAuthDiscoveryDone(false);
    setAuthorization(null);
  };

  // Re-seed the form when the dialog opens so Refresh keeps the existing use case.
  useEffect(() => {
    if (isOpen) {
      form.reset(getConnectMCPServerDialogDefaultValues(initialUseCase));
    }
  }, [isOpen, initialUseCase, form]);

  // Synchronous validation — no race condition with useEffect.
  const credentialError = useMemo(
    () =>
      authorization
        ? validateOAuthCredentials({
            provider: authorization.provider,
            useCase: useCase ?? null,
            authCredentials: authCredentials ?? null,
          })
        : null,
    [authorization, useCase, authCredentials]
  );

  const handleOAuthSave = async (values: MCPServerOAuthFormValues) => {
    if (!authorization) {
      return;
    }

    // Guard: handleSubmit only checks Zod schema errors, not manual setError errors.
    if (credentialError) {
      return;
    }

    setIsLoading(true);
    const submitRes = await submitConnectMCPServerDialogForm({
      owner,
      mcpServerView,
      authorization,
      values,
      createMCPServerConnection,
      updateServerView,
      onBeforeAssociateConnection: () => setExternalIsLoading(true),
      cellInfo: cellContext.cellInfo,
    });

    if (submitRes.isErr()) {
      const providerName = OAUTH_PROVIDER_NAMES[authorization.provider];
      sendApiErrorNotification({
        title: t`Failed to connect ${providerName}`,
        error: submitRes.error,
      });
      setIsLoading(false);
      return;
    }

    setExternalIsLoading(false);
    setIsLoading(false);
    setIsOpen(false);
    resetState();
  };

  const staticFormRef = useRef<StaticCredentialFormHandle>(null);
  const [isStaticFormValid, setIsStaticFormValid] = useState(false);

  const staticFormComponent =
    authorization && useCase
      ? getStaticCredentialForm(authorization.provider, useCase)
      : null;
  const hasStaticForm = !!staticFormComponent;

  const staticCredentialConfig: StaticCredentialConfig | undefined =
    staticFormComponent
      ? {
          owner,
          formRef: staticFormRef,
          onValidityChange: setIsStaticFormValid,
          FormComponent: staticFormComponent,
        }
      : undefined;

  // Form is valid when: use case selected AND either OAuth or static form is valid.
  const isFormValid =
    !!useCase && (hasStaticForm ? isStaticFormValid : !credentialError);

  const handleStaticCredentialSave = async () => {
    if (!authorization || !useCase) {
      return;
    }

    setIsLoading(true);
    setExternalIsLoading(true);

    try {
      const formHandle = staticFormRef.current;
      if (!formHandle) {
        sendNotification({
          type: "error",
          title: t`Cannot submit credentials`,
          description: t`The credentials form is not ready. Please retry.`,
        });
        datadogLogger.error(
          {
            workspaceId: owner.sId,
            mcpServerId: mcpServerView.server.sId,
            hasAuthorization: !!authorization,
            useCase,
          },
          "Static credential form ref is null at submit time"
        );
        return;
      }

      const credentialId = await formHandle.submit();
      if (!credentialId) {
        // The form surfaced its own notification for the specific failure;
        // log here so we also have a parent-side breadcrumb.
        datadogLogger.warn(
          {
            workspaceId: owner.sId,
            mcpServerId: mcpServerView.server.sId,
          },
          "Static credential form submit returned null"
        );
        return;
      }

      const connectionCreationRes = await createMCPServerConnection({
        credentialId,
        mcpServerId: mcpServerView.server.sId,
        mcpServerDisplayName: getMcpServerViewDisplayName(mcpServerView),
        provider: authorization.provider,
      });
      if (!connectionCreationRes) {
        datadogLogger.error(
          {
            workspaceId: owner.sId,
            mcpServerId: mcpServerView.server.sId,
            credentialId,
          },
          "createMCPServerConnection returned falsy result"
        );
        return;
      }

      const updateServerViewRes = await updateServerView({
        oAuthUseCase: useCase,
      });
      if (!updateServerViewRes) {
        datadogLogger.error(
          {
            workspaceId: owner.sId,
            mcpServerId: mcpServerView.server.sId,
          },
          "updateServerView returned falsy result"
        );
        return;
      }

      setIsOpen(false);
      resetState();
    } catch (err) {
      const e = normalizeError(err);
      sendNotification({
        type: "error",
        title: t`Failed to connect the tool`,
        description: e.message,
      });
      datadogLogger.error(
        { workspaceId: owner.sId, err: e },
        "Unexpected error in handleStaticCredentialSave"
      );
    } finally {
      setIsLoading(false);
      setExternalIsLoading(false);
    }
  };

  const handleRightButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasStaticForm) {
      void handleStaticCredentialSave();
    } else {
      void form.handleSubmit(handleOAuthSave)();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        resetState();
      }}
    >
      <DialogContent size="lg">
        <FormProvider form={form} asForm={false}>
          <DialogHeader>
            <DialogTitle visual={getAvatarFromIcon(toolIcon, "sm")}>
              <Trans>Connect {toolName}</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-5 py-4">
            {authorization && (
              <MCPServerAuthConnection
                toolName={toolName}
                authorization={authorization}
                documentationUrl={
                  mcpServerView.server?.documentationUrl ?? undefined
                }
                staticCredentialConfig={staticCredentialConfig}
                lockUseCase={lockUseCase}
              />
            )}
          </div>
          <DialogFooter
            leftButtonProps={{
              label: t`Cancel`,
              variant: "ghost",
              onClick: resetState,
            }}
            rightButtonProps={
              authorization
                ? {
                    isLoading: isLoading,
                    label: hasStaticForm ? t`Connect` : t`Setup connection`,
                    variant: "primary",
                    onClick: handleRightButtonClick,
                    disabled: !isFormValid || isLoading,
                  }
                : undefined
            }
          />
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
